'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

type LoginStep = 'credentials' | 'totp' | 'reset';

const MAX_ATTEMPTS = 5;
const LOCKOUT_MINUTES = 15;

// Demo accounts for quick testing
const DEMO_ACCOUNTS = [
  {
    label: 'Admin Lab',
    email: 'admin@labflow.demo',
    password: 'Demo1234!',
    role: 'Amministratore',
    color: 'teal',
  },
  {
    label: 'Biologo',
    email: 'biologo@labflow.demo',
    password: 'Demo1234!',
    role: 'Biologo',
    color: 'blue',
  },
  {
    label: 'Accettazione',
    email: 'accettazione@labflow.demo',
    password: 'Demo1234!',
    role: 'Accettazione',
    color: 'violet',
  },
];

// TOTP verification using HMAC-SHA1 (RFC 6238)
async function verifyTOTP(secret: string, token: string): Promise<boolean> {
  try {
    const base32Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let bits = 0;
    let value = 0;
    const bytes: number[] = [];
    for (const char of secret.toUpperCase().replace(/=+$/, '')) {
      const idx = base32Chars.indexOf(char);
      if (idx === -1) continue;
      value = (value << 5) | idx;
      bits += 5;
      if (bits >= 8) {
        bytes.push((value >>> (bits - 8)) & 255);
        bits -= 8;
      }
    }
    const keyBytes = new Uint8Array(bytes);
    const counter = Math.floor(Date.now() / 1000 / 30);
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'HMAC', hash: 'SHA-1' },
      false,
      ['sign']
    );
    for (let drift = -1; drift <= 1; drift++) {
      const counterVal = counter + drift;
      const counterBytes = new Uint8Array(8);
      let c = counterVal;
      for (let i = 7; i >= 0; i--) {
        counterBytes[i] = c & 0xff;
        c = c >> 8;
      }
      const buf = new DataView(new ArrayBuffer(8));
      buf.setUint32(4, counterVal >>> 0, false);
      const sig = await crypto.subtle.sign('HMAC', cryptoKey, buf.buffer);
      const arr = new Uint8Array(sig);
      const offset = arr[19] & 0xf;
      const otp =
        (((arr[offset] & 0x7f) << 24) |
          (arr[offset + 1] << 16) |
          (arr[offset + 2] << 8) |
          arr[offset + 3]) %
        1000000;
      if (otp.toString().padStart(6, '0') === token.trim()) return true;
    }
    return false;
  } catch {
    return false;
  }
}

// Remove the invalid metadata export — metadata is handled by layout.tsx
// export const metadata = { ... }  ← removed (cannot export from 'use client')

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const supabase = createClient();

  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingTotpSecret, setPendingTotpSecret] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string>('');
  const [pendingPassword, setPendingPassword] = useState<string>('');
  const [demoLoading, setDemoLoading] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  const isLocked = lockedUntil && new Date() < lockedUntil;

  const recordLoginAttempt = async (success: boolean) => {
    try {
      await supabase.from('login_attempts').insert({ email, success, ip_address: null });
    } catch {
      /* silently fail */
    }
  };

  const checkLockout = async (emailToCheck: string): Promise<boolean> => {
    try {
      const { data } = await supabase
        .from('account_lockouts')
        .select('*')
        .eq('email', emailToCheck)
        .single();
      if (data && data.locked_until && new Date(data.locked_until) > new Date()) {
        setLockedUntil(new Date(data.locked_until));
        return true;
      }
    } catch {
      /* no lockout record */
    }
    return false;
  };

  const recordFailedAttempt = async () => {
    const newCount = failedAttempts + 1;
    setFailedAttempts(newCount);
    if (newCount >= MAX_ATTEMPTS) {
      const until = new Date(Date.now() + LOCKOUT_MINUTES * 60 * 1000);
      setLockedUntil(until);
      try {
        await supabase.from('account_lockouts').upsert(
          {
            email,
            locked_at: new Date().toISOString(),
            locked_until: until.toISOString(),
            failed_attempts: newCount,
          },
          { onConflict: 'email' }
        );
      } catch {
        /* silently fail */
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Client-side field validation
    const newFieldErrors: { email?: string; password?: string } = {};
    if (!email.trim()) newFieldErrors.email = 'Email obbligatoria';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      newFieldErrors.email = 'Formato email non valido';
    if (!password) newFieldErrors.password = 'Password obbligatoria';
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      return;
    }
    setFieldErrors({});
    if (isLocked) {
      const mins = Math.ceil((lockedUntil!.getTime() - Date.now()) / 60000);
      setError(`Account bloccato. Riprova tra ${mins} minuti o usa il reset password.`);
      return;
    }
    setLoading(true);
    try {
      const locked = await checkLockout(email);
      if (locked) {
        setError(
          `Account temporaneamente bloccato per troppi tentativi falliti. Riprova tra ${LOCKOUT_MINUTES} minuti.`
        );
        return;
      }
      await signIn(email, password);
      await recordLoginAttempt(true);

      // MFA/TOTP TEMPORANEAMENTE DISABILITATO
      // Per riabilitare, decommentare il blocco seguente e rimuovere il redirect diretto:
      //
      // const { data: { user } } = await supabase.auth.getUser();
      // if (user) {
      //   const { data: profile } = await supabase.from('user_profiles').select('totp_enabled, totp_secret, id').eq('id', user.id).single();
      //   if (profile?.totp_enabled && profile?.totp_secret) {
      //     setPendingUserId(user.id);
      //     setPendingTotpSecret(profile.totp_secret);
      //     setPendingEmail(email);
      //     setPendingPassword(password);
      //     setStep('totp');
      //     await supabase.auth.signOut();
      //     return;
      //   }
      // }

      await supabase.from('account_lockouts').delete().eq('email', email);
      router.push('/dashboard');
      router.refresh();
    } catch (err: any) {
      await recordLoginAttempt(false);
      await recordFailedAttempt();
      const remaining = MAX_ATTEMPTS - (failedAttempts + 1);
      if (remaining > 0) {
        setError(
          `Credenziali non valide. ${remaining} tentativ${remaining === 1 ? 'o' : 'i'} rimanent${remaining === 1 ? 'e' : 'i'} prima del blocco.`
        );
      } else {
        setError(`Account bloccato per ${LOCKOUT_MINUTES} minuti dopo troppi tentativi falliti.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!pendingTotpSecret) {
      setError('Sessione scaduta. Riaccedi.');
      setStep('credentials');
      return;
    }
    if (!pendingEmail || !pendingPassword) {
      setError('Sessione scaduta. Riaccedi.');
      setStep('credentials');
      return;
    }
    setLoading(true);
    try {
      const valid = await verifyTOTP(pendingTotpSecret, totpCode);
      if (!valid) {
        setError('Codice TOTP non valido. Riprova.');
        return;
      }
      // Re-authenticate using stored credentials (not stale email/password state)
      await signIn(pendingEmail, pendingPassword);
      await supabase.from('account_lockouts').delete().eq('email', pendingEmail);
      // Clear sensitive pending state
      setPendingEmail('');
      setPendingPassword('');
      setPendingTotpSecret(null);
      setPendingUserId(null);
      router.push('/dashboard');
      router.refresh();
    } catch (err: any) {
      setError(err.message || 'Errore verifica TOTP');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      setSuccessMsg(`Email di reset inviata a ${resetEmail}. Controlla la tua casella di posta.`);
    } catch (err: any) {
      setError(err.message || "Errore nell'invio dell'email di reset.");
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (account: (typeof DEMO_ACCOUNTS)[0]) => {
    setError('');
    setDemoLoading(account.email);
    setEmail(account.email);
    setPassword(account.password);
    try {
      await signIn(account.email, account.password);
      router.push('/dashboard');
      router.refresh();
    } catch (err: any) {
      setError(
        `Account demo non disponibile: ${err.message || 'Credenziali non valide'}. Assicurati che l'utente demo esista in Supabase.`
      );
    } finally {
      setDemoLoading(null);
    }
  };

  return (
    <div data-component-id="src/app/login/page.tsx:307:4" data-component-path="src/app/login/page.tsx" data-component-line="307" data-component-end-line="812" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-gradient-to-br%20from-slate-900%20via-slate-800%20to-teal-900%20flex%20items-center%20justify-center%20p-4%22%7D" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
      {/* Skip to main content — WCAG 2.4.1 */}
      <a data-component-id="src/app/login/page.tsx:309:6" data-component-path="src/app/login/page.tsx" data-component-line="309" data-component-end-line="314" data-component-file="page.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22%23login-main%22%2C%22className%22%3A%22sr-only%20focus%3Anot-sr-only%20focus%3Aabsolute%20focus%3Atop-4%20focus%3Aleft-4%20focus%3Az-50%20focus%3Apx-4%20focus%3Apy-2%20focus%3Abg-white%20focus%3Atext-teal-700%20focus%3Arounded-lg%20focus%3Afont-medium%20focus%3Ashadow-lg%22%2C%22textContent%22%3A%22Vai%20al%20contenuto%20principale%22%7D"
        href="#login-main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-teal-700 focus:rounded-lg focus:font-medium focus:shadow-lg"
      >
        Vai al contenuto principale
      </a>
      <div data-component-id="src/app/login/page.tsx:315:6" data-component-path="src/app/login/page.tsx" data-component-line="315" data-component-end-line="811" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20max-w-md%22%7D" className="w-full max-w-md">
        {/* Logo */}
        <div data-component-id="src/app/login/page.tsx:317:8" data-component-path="src/app/login/page.tsx" data-component-line="317" data-component-end-line="323" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20mb-8%22%7D" className="flex flex-col items-center mb-8" aria-hidden="true">
          <div data-component-id="src/app/login/page.tsx:318:10" data-component-path="src/app/login/page.tsx" data-component-line="318" data-component-end-line="321" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20mb-2%22%7D" className="flex items-center gap-3 mb-2">
            <AppLogo data-component-id="src/app/login/page.tsx:319:12" data-component-path="src/app/login/page.tsx" data-component-line="319" data-component-end-line="319" data-component-file="page.tsx" data-component-name="AppLogo" data-component-content="%7B%22elementName%22%3A%22AppLogo%22%7D" size={40} />
            <span data-component-id="src/app/login/page.tsx:320:12" data-component-path="src/app/login/page.tsx" data-component-line="320" data-component-end-line="320" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-white%20tracking-tight%22%2C%22textContent%22%3A%22LabFlow%22%7D" className="text-2xl font-bold text-white tracking-tight">LabFlow</span>
          </div>
          <p data-component-id="src/app/login/page.tsx:322:10" data-component-path="src/app/login/page.tsx" data-component-line="322" data-component-end-line="322" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-slate-400%20text-sm%22%2C%22textContent%22%3A%22Sistema%20Informativo%20di%20Laboratorio%22%7D" className="text-slate-400 text-sm">Sistema Informativo di Laboratorio</p>
        </div>

        {/* Card */}
        <main data-component-id="src/app/login/page.tsx:326:8" data-component-path="src/app/login/page.tsx" data-component-line="326" data-component-end-line="810" data-component-file="page.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22id%22%3A%22login-main%22%7D" id="login-main">
          <div data-component-id="src/app/login/page.tsx:327:10" data-component-path="src/app/login/page.tsx" data-component-line="327" data-component-end-line="809" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-2xl%20shadow-2xl%20p-8%22%7D" className="bg-white rounded-2xl shadow-2xl p-8">
            {/* STEP: Credentials */}
            {step === 'credentials' && (
              <>
                <h1 data-component-id="src/app/login/page.tsx:331:16" data-component-path="src/app/login/page.tsx" data-component-line="331" data-component-end-line="333" data-component-file="page.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-slate-900%20mb-1%22%2C%22textContent%22%3A%22Accedi%20al%20tuo%20laboratorio%22%7D" className="text-xl font-semibold text-slate-900 mb-1">
                  Accedi al tuo laboratorio
                </h1>
                <p data-component-id="src/app/login/page.tsx:334:16" data-component-path="src/app/login/page.tsx" data-component-line="334" data-component-end-line="336" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-slate-500%20mb-6%22%2C%22textContent%22%3A%22Inserisci%20le%20tue%20credenziali%20per%20continuare%22%7D" className="text-sm text-slate-500 mb-6">
                  Inserisci le tue credenziali per continuare
                </p>

                {isLocked && (
                  <div data-component-id="src/app/login/page.tsx:339:18" data-component-path="src/app/login/page.tsx" data-component-line="339" data-component-end-line="363" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%20p-3%20bg-red-50%20border%20border-red-200%20rounded-lg%20text-sm%20text-red-700%20flex%20items-start%20gap-2%22%7D"
                    role="alert"
                    aria-live="assertive"
                    className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2"
                  >
                    <svg data-component-id="src/app/login/page.tsx:344:20" data-component-path="src/app/login/page.tsx" data-component-line="344" data-component-end-line="357" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-4%20h-4%20mt-0.5%20shrink-0%22%7D"
                      className="w-4 h-4 mt-0.5 shrink-0"
                      aria-hidden="true"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path data-component-id="src/app/login/page.tsx:351:22" data-component-path="src/app/login/page.tsx" data-component-line="351" data-component-end-line="356" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    <span data-component-id="src/app/login/page.tsx:358:20" data-component-path="src/app/login/page.tsx" data-component-line="358" data-component-end-line="362" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Account%20bloccato.%20Riprova%20tra%20%20%20minuti%20o%20usa%20il%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20reset%20password.%22%7D">
                      Account bloccato. Riprova tra{' '}
                      {Math.ceil((lockedUntil!.getTime() - Date.now()) / 60000)} minuti o usa il
                      reset password.
                    </span>
                  </div>
                )}

                {error && !isLocked && (
                  <div data-component-id="src/app/login/page.tsx:367:18" data-component-path="src/app/login/page.tsx" data-component-line="367" data-component-end-line="373" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%20p-3%20bg-red-50%20border%20border-red-200%20rounded-lg%20text-sm%20text-red-700%22%7D"
                    className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
                    role="alert"
                    aria-live="polite"
                  >
                    {error}
                  </div>
                )}

                {failedAttempts > 0 && failedAttempts < MAX_ATTEMPTS && (
                  <div data-component-id="src/app/login/page.tsx:377:18" data-component-path="src/app/login/page.tsx" data-component-line="377" data-component-end-line="400" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%20p-2%20bg-amber-50%20border%20border-amber-200%20rounded-lg%22%7D"
                    className="mb-4 p-2 bg-amber-50 border border-amber-200 rounded-lg"
                    role="status"
                    aria-live="polite"
                  >
                    <div data-component-id="src/app/login/page.tsx:382:20" data-component-path="src/app/login/page.tsx" data-component-line="382" data-component-end-line="386" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-1%22%7D" className="flex items-center justify-between mb-1">
                      <span data-component-id="src/app/login/page.tsx:383:22" data-component-path="src/app/login/page.tsx" data-component-line="383" data-component-end-line="385" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-amber-700%20font-medium%22%2C%22textContent%22%3A%22Tentativi%20falliti%3A%20%2F%22%7D" className="text-xs text-amber-700 font-medium">
                        Tentativi falliti: {failedAttempts}/{MAX_ATTEMPTS}
                      </span>
                    </div>
                    <div data-component-id="src/app/login/page.tsx:387:20" data-component-path="src/app/login/page.tsx" data-component-line="387" data-component-end-line="399" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20bg-amber-100%20rounded-full%20h-1.5%22%7D"
                      className="w-full bg-amber-100 rounded-full h-1.5"
                      role="progressbar"
                      aria-valuenow={failedAttempts}
                      aria-valuemin={0}
                      aria-valuemax={MAX_ATTEMPTS}
                      aria-label={`Tentativi falliti: ${failedAttempts} su ${MAX_ATTEMPTS}`}
                    >
                      <div data-component-id="src/app/login/page.tsx:395:22" data-component-path="src/app/login/page.tsx" data-component-line="395" data-component-end-line="398" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-amber-500%20h-1.5%20rounded-full%20transition-all%22%7D"
                        className="bg-amber-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${(failedAttempts / MAX_ATTEMPTS) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                <form data-component-id="src/app/login/page.tsx:403:16" data-component-path="src/app/login/page.tsx" data-component-line="403" data-component-end-line="539" data-component-file="page.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22space-y-4%22%7D"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  noValidate
                  aria-label="Modulo di accesso"
                >
                  <div data-component-id="src/app/login/page.tsx:409:18" data-component-path="src/app/login/page.tsx" data-component-line="409" data-component-end-line="438" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                    <label data-component-id="src/app/login/page.tsx:410:20" data-component-path="src/app/login/page.tsx" data-component-line="410" data-component-end-line="415" data-component-file="page.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-slate-700%20mb-1%22%2C%22textContent%22%3A%22Email%22%7D"
                      htmlFor="login-email"
                      className="block text-sm font-medium text-slate-700 mb-1"
                    >
                      Email
                    </label>
                    <input data-component-id="src/app/login/page.tsx:416:20" data-component-path="src/app/login/page.tsx" data-component-line="416" data-component-end-line="432" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22id%22%3A%22login-email%22%2C%22type%22%3A%22email%22%2C%22value%22%3A%22%5Bvar%3Aemail%5D%22%7D"
                      id="login-email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, email: undefined }));
                      }}
                      required
                      disabled={!!isLocked}
                      placeholder="nome@laboratorio.it"
                      autoComplete="email"
                      aria-required="true"
                      aria-invalid={!!fieldErrors.email}
                      aria-describedby={fieldErrors.email ? 'login-email-error' : undefined}
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-slate-50 disabled:text-slate-400 ${fieldErrors.email ? 'border-red-400' : 'border-slate-300'}`}
                    />
                    {fieldErrors.email && (
                      <p data-component-id="src/app/login/page.tsx:434:22" data-component-path="src/app/login/page.tsx" data-component-line="434" data-component-end-line="436" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22id%22%3A%22login-email-error%22%2C%22className%22%3A%22text-xs%20text-red-600%20mt-1%22%7D" id="login-email-error" className="text-xs text-red-600 mt-1" role="alert">
                        {fieldErrors.email}
                      </p>
                    )}
                  </div>
                  <div data-component-id="src/app/login/page.tsx:439:18" data-component-path="src/app/login/page.tsx" data-component-line="439" data-component-end-line="485" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                    <div data-component-id="src/app/login/page.tsx:440:20" data-component-path="src/app/login/page.tsx" data-component-line="440" data-component-end-line="458" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-1%22%7D" className="flex items-center justify-between mb-1">
                      <label data-component-id="src/app/login/page.tsx:441:22" data-component-path="src/app/login/page.tsx" data-component-line="441" data-component-end-line="446" data-component-file="page.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-slate-700%22%2C%22textContent%22%3A%22Password%22%7D"
                        htmlFor="login-password"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Password
                      </label>
                      <button data-component-id="src/app/login/page.tsx:447:22" data-component-path="src/app/login/page.tsx" data-component-line="447" data-component-end-line="457" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22text-xs%20text-teal-600%20hover%3Atext-teal-700%20font-medium%20focus%3Aoutline-none%20focus%3Aunderline%22%2C%22textContent%22%3A%22Password%20dimenticata%3F%22%7D"
                        type="button"
                        onClick={() => {
                          setResetEmail(email);
                          setStep('reset');
                          setError('');
                        }}
                        className="text-xs text-teal-600 hover:text-teal-700 font-medium focus:outline-none focus:underline"
                      >
                        Password dimenticata?
                      </button>
                    </div>
                    <input data-component-id="src/app/login/page.tsx:459:20" data-component-path="src/app/login/page.tsx" data-component-line="459" data-component-end-line="475" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22id%22%3A%22login-password%22%2C%22type%22%3A%22password%22%2C%22value%22%3A%22%5Bvar%3Apassword%5D%22%7D"
                      id="login-password"
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, password: undefined }));
                      }}
                      required
                      disabled={!!isLocked}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      aria-required="true"
                      aria-invalid={!!fieldErrors.password}
                      aria-describedby={fieldErrors.password ? 'login-password-error' : undefined}
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-slate-50 disabled:text-slate-400 ${fieldErrors.password ? 'border-red-400' : 'border-slate-300'}`}
                    />
                    {fieldErrors.password && (
                      <p data-component-id="src/app/login/page.tsx:477:22" data-component-path="src/app/login/page.tsx" data-component-line="477" data-component-end-line="483" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22id%22%3A%22login-password-error%22%2C%22className%22%3A%22text-xs%20text-red-600%20mt-1%22%7D"
                        id="login-password-error"
                        className="text-xs text-red-600 mt-1"
                        role="alert"
                      >
                        {fieldErrors.password}
                      </p>
                    )}
                  </div>
                  <button data-component-id="src/app/login/page.tsx:486:18" data-component-path="src/app/login/page.tsx" data-component-line="486" data-component-end-line="538" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22w-full%20bg-teal-700%20hover%3Abg-teal-800%20disabled%3Abg-teal-400%20text-white%20font-medium%20py-2.5%20px-4%20rounded-lg%20text-sm%20transition-all%20duration-150%20flex%20items-center%20justify-center%20gap-2%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-teal-500%20focus%3Aring-offset-2%22%7D"
                    type="submit"
                    disabled={loading || !!isLocked}
                    aria-busy={loading}
                    aria-disabled={!!isLocked}
                    className="w-full bg-teal-700 hover:bg-teal-800 disabled:bg-teal-400 text-white font-medium py-2.5 px-4 rounded-lg text-sm transition-all duration-150 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                  >
                    {loading ? (
                      <>
                        <svg data-component-id="src/app/login/page.tsx:495:24" data-component-path="src/app/login/page.tsx" data-component-line="495" data-component-end-line="514" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22animate-spin%20h-4%20w-4%22%7D"
                          className="animate-spin h-4 w-4"
                          aria-hidden="true"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle data-component-id="src/app/login/page.tsx:501:26" data-component-path="src/app/login/page.tsx" data-component-line="501" data-component-end-line="508" data-component-file="page.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%2C%22className%22%3A%22opacity-25%22%7D"
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path data-component-id="src/app/login/page.tsx:509:26" data-component-path="src/app/login/page.tsx" data-component-line="509" data-component-end-line="513" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%2C%22className%22%3A%22opacity-75%22%7D"
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        <span data-component-id="src/app/login/page.tsx:515:24" data-component-path="src/app/login/page.tsx" data-component-line="515" data-component-end-line="515" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Accesso%20in%20corso...%22%7D">Accesso in corso...</span>
                      </>
                    ) : isLocked ? (
                      <>
                        <svg data-component-id="src/app/login/page.tsx:519:24" data-component-path="src/app/login/page.tsx" data-component-line="519" data-component-end-line="532" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-4%20h-4%22%7D"
                          className="w-4 h-4"
                          aria-hidden="true"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path data-component-id="src/app/login/page.tsx:526:26" data-component-path="src/app/login/page.tsx" data-component-line="526" data-component-end-line="531" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                        Account bloccato
                      </>
                    ) : (
                      'Accedi'
                    )}
                  </button>
                </form>

                {/* Quick Demo Login */}
                <div data-component-id="src/app/login/page.tsx:542:16" data-component-path="src/app/login/page.tsx" data-component-line="542" data-component-end-line="637" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-5%22%7D" className="mt-5">
                  <div data-component-id="src/app/login/page.tsx:543:18" data-component-path="src/app/login/page.tsx" data-component-line="543" data-component-end-line="562" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20mb-3%22%7D" className="flex items-center gap-2 mb-3" aria-hidden="true">
                    <div data-component-id="src/app/login/page.tsx:544:20" data-component-path="src/app/login/page.tsx" data-component-line="544" data-component-end-line="544" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20h-px%20bg-slate-200%22%7D" className="flex-1 h-px bg-slate-200" />
                    <span data-component-id="src/app/login/page.tsx:545:20" data-component-path="src/app/login/page.tsx" data-component-line="545" data-component-end-line="560" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-slate-400%20font-medium%20px-1%20flex%20items-center%20gap-1%22%2C%22textContent%22%3A%22Accesso%20rapido%20demo%22%7D" className="text-xs text-slate-400 font-medium px-1 flex items-center gap-1">
                      <svg data-component-id="src/app/login/page.tsx:546:22" data-component-path="src/app/login/page.tsx" data-component-line="546" data-component-end-line="558" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-3%20h-3%22%7D"
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path data-component-id="src/app/login/page.tsx:552:24" data-component-path="src/app/login/page.tsx" data-component-line="552" data-component-end-line="557" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      Accesso rapido demo
                    </span>
                    <div data-component-id="src/app/login/page.tsx:561:20" data-component-path="src/app/login/page.tsx" data-component-line="561" data-component-end-line="561" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20h-px%20bg-slate-200%22%7D" className="flex-1 h-px bg-slate-200" />
                  </div>
                  <div data-component-id="src/app/login/page.tsx:563:18" data-component-path="src/app/login/page.tsx" data-component-line="563" data-component-end-line="627" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-3%20gap-2%22%7D"
                    className="grid grid-cols-3 gap-2"
                    role="group"
                    aria-label="Account demo disponibili"
                  >
                    {DEMO_ACCOUNTS.map((account) => (
                      <button data-component-id="src/app/login/page.tsx:569:22" data-component-path="src/app/login/page.tsx" data-component-line="569" data-component-end-line="625" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20gap-1%20p-2.5%20rounded-lg%20border%20border-slate-200%20hover%3Aborder-teal-300%20hover%3Abg-teal-50%20transition-all%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20group%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-teal-500%22%7D"
                        key={account.email}
                        type="button"
                        onClick={() => handleDemoLogin(account)}
                        disabled={!!demoLoading || !!isLocked}
                        aria-label={`Accedi come ${account.label} — ruolo ${account.role}`}
                        aria-busy={demoLoading === account.email}
                        className="flex flex-col items-center gap-1 p-2.5 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        {demoLoading === account.email ? (
                          <svg data-component-id="src/app/login/page.tsx:579:26" data-component-path="src/app/login/page.tsx" data-component-line="579" data-component-end-line="598" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22animate-spin%20h-4%20w-4%20text-teal-600%22%7D"
                            className="animate-spin h-4 w-4 text-teal-600"
                            aria-hidden="true"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle data-component-id="src/app/login/page.tsx:585:28" data-component-path="src/app/login/page.tsx" data-component-line="585" data-component-end-line="592" data-component-file="page.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%2C%22className%22%3A%22opacity-25%22%7D"
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path data-component-id="src/app/login/page.tsx:593:28" data-component-path="src/app/login/page.tsx" data-component-line="593" data-component-end-line="597" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%2C%22className%22%3A%22opacity-75%22%7D"
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        ) : (
                          <div data-component-id="src/app/login/page.tsx:600:26" data-component-path="src/app/login/page.tsx" data-component-line="600" data-component-end-line="617" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-7%20h-7%20rounded-full%20bg-slate-100%20group-hover%3Abg-teal-100%20flex%20items-center%20justify-center%20transition-colors%22%7D"
                            className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-teal-100 flex items-center justify-center transition-colors"
                            aria-hidden="true"
                          >
                            <svg data-component-id="src/app/login/page.tsx:604:28" data-component-path="src/app/login/page.tsx" data-component-line="604" data-component-end-line="616" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-3.5%20h-3.5%20text-slate-500%20group-hover%3Atext-teal-700%22%7D"
                              className="w-3.5 h-3.5 text-slate-500 group-hover:text-teal-700"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path data-component-id="src/app/login/page.tsx:610:30" data-component-path="src/app/login/page.tsx" data-component-line="610" data-component-end-line="615" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                          </div>
                        )}
                        <span data-component-id="src/app/login/page.tsx:619:24" data-component-path="src/app/login/page.tsx" data-component-line="619" data-component-end-line="621" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-medium%20text-slate-700%20group-hover%3Atext-teal-800%20leading-tight%20text-center%22%7D" className="text-xs font-medium text-slate-700 group-hover:text-teal-800 leading-tight text-center">
                          {account.label}
                        </span>
                        <span data-component-id="src/app/login/page.tsx:622:24" data-component-path="src/app/login/page.tsx" data-component-line="622" data-component-end-line="624" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B10px%5D%20text-slate-400%20group-hover%3Atext-teal-600%20leading-tight%20text-center%22%7D" className="text-[10px] text-slate-400 group-hover:text-teal-600 leading-tight text-center">
                          {account.role}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p data-component-id="src/app/login/page.tsx:628:18" data-component-path="src/app/login/page.tsx" data-component-line="628" data-component-end-line="636" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-center%20text-sm%20text-slate-500%20mt-6%22%2C%22textContent%22%3A%22Non%20hai%20un%20account%3F%22%7D" className="text-center text-sm text-slate-500 mt-6">
                    Non hai un account?{' '}
                    <Link data-component-id="src/app/login/page.tsx:630:20" data-component-path="src/app/login/page.tsx" data-component-line="630" data-component-end-line="635" data-component-file="page.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fregister%22%2C%22className%22%3A%22text-teal-600%20hover%3Atext-teal-700%20font-medium%20focus%3Aoutline-none%20focus%3Aunderline%22%2C%22textContent%22%3A%22Registrati%22%7D"
                      href="/register"
                      className="text-teal-600 hover:text-teal-700 font-medium focus:outline-none focus:underline"
                    >
                      Registrati
                    </Link>
                  </p>
                </div>
              </>
            )}

            {/* STEP: TOTP — TEMPORANEAMENTE DISABILITATO */}
            {/* Per riabilitare: rimuovere il commento e riabilitare il blocco TOTP in handleSubmit */}
            {/*
          {step === 'totp' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Verifica 2FA</h2>
                  <p className="text-sm text-slate-500">Inserisci il codice dalla tua app autenticatore</p>
                </div>
              </div>

              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

              <form onSubmit={handleTOTPVerify} className="space-y-4">
                <div>
                  <label htmlFor="totp-code" className="block text-sm font-medium text-slate-700 mb-1">Codice TOTP (6 cifre)</label>
                  <input
                    id="totp-code" type="text" inputMode="numeric" pattern="[0-9]{6}"
                    value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" autoComplete="one-time-code" maxLength={6}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <button type="submit" disabled={loading || totpCode.length !== 6}
                  className="w-full bg-teal-700 hover:bg-teal-800 disabled:bg-teal-400 text-white font-medium py-2.5 px-4 rounded-lg text-sm transition-all flex items-center justify-center gap-2">
                  {loading ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Verifica...</> : 'Verifica codice'}
                </button>
              </form>
              <button onClick={() => { setStep('credentials'); setTotpCode(''); setError(''); }} className="w-full mt-3 text-sm text-slate-500 hover:text-slate-700 text-center">
                ← Torna al login
              </button>
            </>
          )}
          */}

            {/* STEP: Reset Password */}
            {step === 'reset' && (
              <>
                <div data-component-id="src/app/login/page.tsx:683:16" data-component-path="src/app/login/page.tsx" data-component-line="683" data-component-end-line="712" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20mb-6%22%7D" className="flex items-center gap-3 mb-6">
                  <button data-component-id="src/app/login/page.tsx:684:18" data-component-path="src/app/login/page.tsx" data-component-line="684" data-component-end-line="707" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22p-2%20rounded-lg%20hover%3Abg-slate-100%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-teal-500%22%7D"
                    onClick={() => {
                      setStep('credentials');
                      setError('');
                      setSuccessMsg('');
                    }}
                    className="p-2 rounded-lg hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    aria-label="Torna al login"
                  >
                    <svg data-component-id="src/app/login/page.tsx:693:20" data-component-path="src/app/login/page.tsx" data-component-line="693" data-component-end-line="706" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%20text-slate-600%22%7D"
                      className="w-5 h-5 text-slate-600"
                      aria-hidden="true"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path data-component-id="src/app/login/page.tsx:700:22" data-component-path="src/app/login/page.tsx" data-component-line="700" data-component-end-line="705" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <div data-component-id="src/app/login/page.tsx:708:18" data-component-path="src/app/login/page.tsx" data-component-line="708" data-component-end-line="711" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                    <h2 data-component-id="src/app/login/page.tsx:709:20" data-component-path="src/app/login/page.tsx" data-component-line="709" data-component-end-line="709" data-component-file="page.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-slate-900%22%2C%22textContent%22%3A%22Reset%20Password%22%7D" className="text-xl font-semibold text-slate-900">Reset Password</h2>
                    <p data-component-id="src/app/login/page.tsx:710:20" data-component-path="src/app/login/page.tsx" data-component-line="710" data-component-end-line="710" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-slate-500%22%2C%22textContent%22%3A%22Riceverai%20un%20link%20via%20email%22%7D" className="text-sm text-slate-500">Riceverai un link via email</p>
                  </div>
                </div>

                {error && (
                  <div data-component-id="src/app/login/page.tsx:715:18" data-component-path="src/app/login/page.tsx" data-component-line="715" data-component-end-line="720" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%20p-3%20bg-red-50%20border%20border-red-200%20rounded-lg%20text-sm%20text-red-700%22%7D"
                    className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
                    role="alert"
                  >
                    {error}
                  </div>
                )}
                {successMsg && (
                  <div data-component-id="src/app/login/page.tsx:723:18" data-component-path="src/app/login/page.tsx" data-component-line="723" data-component-end-line="743" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%20p-3%20bg-green-50%20border%20border-green-200%20rounded-lg%20text-sm%20text-green-700%20flex%20items-start%20gap-2%22%7D"
                    className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-start gap-2"
                    role="status"
                    aria-live="polite"
                  >
                    <svg data-component-id="src/app/login/page.tsx:728:20" data-component-path="src/app/login/page.tsx" data-component-line="728" data-component-end-line="741" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-4%20h-4%20mt-0.5%20shrink-0%22%7D"
                      className="w-4 h-4 mt-0.5 shrink-0"
                      aria-hidden="true"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path data-component-id="src/app/login/page.tsx:735:22" data-component-path="src/app/login/page.tsx" data-component-line="735" data-component-end-line="740" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {successMsg}
                  </div>
                )}

                {!successMsg && (
                  <form data-component-id="src/app/login/page.tsx:747:18" data-component-path="src/app/login/page.tsx" data-component-line="747" data-component-end-line="805" data-component-file="page.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22space-y-4%22%7D"
                    onSubmit={handlePasswordReset}
                    className="space-y-4"
                    aria-label="Modulo reset password"
                  >
                    <div data-component-id="src/app/login/page.tsx:752:20" data-component-path="src/app/login/page.tsx" data-component-line="752" data-component-end-line="770" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                      <label data-component-id="src/app/login/page.tsx:753:22" data-component-path="src/app/login/page.tsx" data-component-line="753" data-component-end-line="758" data-component-file="page.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-slate-700%20mb-1%22%2C%22textContent%22%3A%22Email%20account%22%7D"
                        htmlFor="reset-email"
                        className="block text-sm font-medium text-slate-700 mb-1"
                      >
                        Email account
                      </label>
                      <input data-component-id="src/app/login/page.tsx:759:22" data-component-path="src/app/login/page.tsx" data-component-line="759" data-component-end-line="769" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22id%22%3A%22reset-email%22%2C%22type%22%3A%22email%22%2C%22value%22%3A%22%5Bvar%3AresetEmail%5D%22%2C%22className%22%3A%22w-full%20px-3%20py-2.5%20border%20border-slate-300%20rounded-lg%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-teal-500%22%7D"
                        id="reset-email"
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        aria-required="true"
                        placeholder="nome@laboratorio.it"
                        autoComplete="email"
                        className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <button data-component-id="src/app/login/page.tsx:771:20" data-component-path="src/app/login/page.tsx" data-component-line="771" data-component-end-line="804" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22w-full%20bg-teal-700%20hover%3Abg-teal-800%20disabled%3Abg-teal-400%20text-white%20font-medium%20py-2.5%20px-4%20rounded-lg%20text-sm%20transition-all%20flex%20items-center%20justify-center%20gap-2%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-teal-500%20focus%3Aring-offset-2%22%7D"
                      type="submit"
                      disabled={loading}
                      aria-busy={loading}
                      className="w-full bg-teal-700 hover:bg-teal-800 disabled:bg-teal-400 text-white font-medium py-2.5 px-4 rounded-lg text-sm transition-all flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    >
                      {loading ? (
                        <>
                          <svg data-component-id="src/app/login/page.tsx:779:26" data-component-path="src/app/login/page.tsx" data-component-line="779" data-component-end-line="798" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22animate-spin%20h-4%20w-4%22%7D"
                            className="animate-spin h-4 w-4"
                            aria-hidden="true"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle data-component-id="src/app/login/page.tsx:785:28" data-component-path="src/app/login/page.tsx" data-component-line="785" data-component-end-line="792" data-component-file="page.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%2C%22className%22%3A%22opacity-25%22%7D"
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path data-component-id="src/app/login/page.tsx:793:28" data-component-path="src/app/login/page.tsx" data-component-line="793" data-component-end-line="797" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%2C%22className%22%3A%22opacity-75%22%7D"
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Invio...
                        </>
                      ) : (
                        'Invia email di reset'
                      )}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
